import { io, Socket } from 'socket.io-client'

/**
 * S0-D (LOT-SUNSET-0) — LE BADGE D'APPELS MANQUÉS SOUS V2.
 *
 * Sous v2, `call:missed` (namespace /calls, émis par les webhooks v1) n'arrive
 * plus jamais : le badge était mort. Ce service écoute son remplaçant,
 * `call.missed`, émis par le gateway /telephony (room tenant) à chaque manqué
 * CONSTATÉ par le flux v2 (refus faute d'agent, sonnerie sans décroché,
 * débordement/abandon de file, reaper).
 *
 * CHOIX D'ARCHITECTURE (documenté, cf. rapport du lot) : le client socket v2
 * existant (`telephony/socket.ts`) est GELÉ et n'expose aucun abonnement
 * générique — ce service ouvre donc SA PROPRE connexion socket.io au namespace
 * /telephony. Coût : une seconde connexion WS par client. Contreparties :
 * aucun octet dans la zone gelée, temps réel vrai (pas de polling), et les
 * rooms sont jointes AU HANDSHAKE côté serveur (JWT) — le client n'a besoin
 * que du token. `calls-socket` (v1) n'est pas réutilisé : il est condamné à
 * terme avec le reste de la v1.
 *
 * Parité assumée avec le comportement v1 : liste du JOUR persistée en
 * localStorage (clé séparée), plafonnée, dédupliquée ; les manqués survenus
 * pendant une déconnexion du socket sont perdus (même limite que la v1).
 */

export interface TelephonyMissedCall {
  /** Id du Call v2 — clé de déduplication et de dismiss. */
  callId: string
  callerNumber: string
  contactId?: string | null
  timestamp: string
}

interface CallMissedEvent {
  callId: string
  tenantId: string
  twilioSid: string | null
  fromNumber: string | null
  contactId: string | null
  occurredAt: string
}

type MissedListener = (call: TelephonyMissedCall) => void

const STORAGE_KEY = 'crm_missed_calls_v2'
const MAX_ENTRIES = 50

class TelephonyMissedCallsService {
  private socket: Socket | null = null
  private missedCalls: TelephonyMissedCall[] = []
  private listeners = new Set<MissedListener>()
  private loaded = false

  /**
   * Idempotent : le badge (monté en permanence dans AppShell) appelle connect
   * à son montage ; une connexion vivante est réutilisée.
   */
  connect(): void {
    this.ensureLoaded()
    if (this.socket) {
      if (this.socket.connected) return
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin
    // Ancré en fin de chaîne : ne pas amputer un domaine contenant "/api" ailleurs.
    const wsUrl = apiUrl.replace(/\/api\/?$/, '')

    this.socket = io(`${wsUrl}/telephony`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      // `auth` en FONCTION : socket.io la rappelle à CHAQUE (re)connexion.
      // Même piège de token périmé après réveil iPad que telephony/socket.ts.
      auth: (cb: (data: { token: string }) => void) => {
        cb({ token: localStorage.getItem('accessToken') || '' })
      },
    })

    this.socket.on('call.missed', (event: CallMissedEvent) => {
      this.record(event)
    })
  }

  disconnect(): void {
    this.socket?.removeAllListeners()
    this.socket?.disconnect()
    this.socket = null
  }

  /** Exposé pour les tests : fait avancer le store sans socket réelle. */
  record(event: CallMissedEvent): void {
    this.ensureLoaded()
    if (!event?.callId) return
    if (this.missedCalls.some((c) => c.callId === event.callId)) return
    const entry: TelephonyMissedCall = {
      callId: event.callId,
      callerNumber: event.fromNumber || 'Inconnu',
      contactId: event.contactId,
      timestamp: event.occurredAt || new Date().toISOString(),
    }
    this.missedCalls.unshift(entry)
    if (this.missedCalls.length > MAX_ENTRIES) {
      this.missedCalls = this.missedCalls.slice(0, MAX_ENTRIES)
    }
    this.save()
    this.listeners.forEach((listener) => listener(entry))
  }

  getMissedCalls(): TelephonyMissedCall[] {
    this.ensureLoaded()
    return [...this.missedCalls]
  }

  markRead(callId: string): void {
    this.missedCalls = this.missedCalls.filter((c) => c.callId !== callId)
    this.save()
  }

  clearAll(): void {
    this.missedCalls = []
    this.save()
  }

  onMissedCall(listener: MissedListener): void {
    this.listeners.add(listener)
  }

  offMissedCall(listener: MissedListener): void {
    this.listeners.delete(listener)
  }

  /** Ne garde que les manqués du JOUR (même règle que la v1). */
  private ensureLoaded(): void {
    if (this.loaded) return
    this.loaded = true
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return
      const parsed = JSON.parse(stored) as TelephonyMissedCall[]
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      this.missedCalls = parsed.filter(
        (c) => c.callId && c.timestamp && new Date(c.timestamp) >= today,
      )
    } catch {
      this.missedCalls = []
    }
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.missedCalls))
    } catch {
      // Stockage indisponible : la liste vit en mémoire pour la session.
    }
  }
}

export const telephonyMissedCalls = new TelephonyMissedCallsService()
